import { Box, Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  CardSection,
  FormActions,
  LogoPickerField,
  MutationButton,
  SelectField,
  StatusPill,
  TextareaField,
  TextField
} from '@/components/common';

export interface OrganisationEditFormOrganisation {
  id: string;
  name: string;
  taxId: string | null;
  address: string | null;
  country: string;
  currency: string;
  logoKey: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  postalCode: string | null;
  contactPerson: string | null;
  website: string | null;
  offerTermsText: string | null;
  iban: string | null;
  bankName: string | null;
  bic: string | null;
  isActive: boolean;
}

const schema = z.object({
  name: z.string().min(1),
  phone: z.string(),
  email: z.union([z.literal(''), z.string().email()]),
  address: z.string(),
  city: z.string(),
  postalCode: z.string(),
  currency: z.string().min(1),
  contactPerson: z.string(),
  website: z.string(),
  offerTermsText: z.string(),
  iban: z.string(),
  bankName: z.string(),
  bic: z.string()
});

export type OrganisationEditFormValues = z.infer<typeof schema>;

interface OrganisationEditFormProps {
  organisation: OrganisationEditFormOrganisation;
  onSubmit: (values: OrganisationEditFormValues) => void;
  onCancel?: () => void;
  showCancel?: boolean;
  loading?: boolean;
  isPaused?: boolean;
  countryOptions: { value: string; label: string }[];
  currencyOptions: { value: string; label: string }[];
  logoSrc?: string | null;
  onLogoSelected: (file: File) => void;
  logoUploading?: boolean;
}

const fieldRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 16,
  alignItems: 'start',
  width: '100%'
} as const;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

const Divider = () => (
  <Box style={{ height: 1, width: '100%', backgroundColor: 'var(--color-border-soft)' }} />
);

const fieldLabelStyle = {
  fz: 11,
  fw: 600,
  tt: 'uppercase' as const,
  style: { letterSpacing: 0.4, color: 'var(--color-text-muted)' }
};

export const OrganisationEditForm = ({
  organisation,
  onSubmit,
  onCancel,
  showCancel = true,
  loading,
  isPaused,
  countryOptions,
  currencyOptions,
  logoSrc,
  onLogoSelected,
  logoUploading
}: OrganisationEditFormProps) => {
  const { t } = useTranslation();

  const form = useForm<OrganisationEditFormValues>({
    validate: zodResolver(schema),
    initialValues: {
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      postalCode: '',
      currency: 'EUR',
      contactPerson: '',
      website: '',
      offerTermsText: '',
      iban: '',
      bankName: '',
      bic: ''
    }
  });

  useEffect(() => {
    const values = {
      name: organisation.name ?? '',
      phone: organisation.phone ?? '',
      email: organisation.email ?? '',
      address: organisation.address ?? '',
      city: organisation.city ?? '',
      postalCode: organisation.postalCode ?? '',
      currency: organisation.currency ?? 'EUR',
      contactPerson: organisation.contactPerson ?? '',
      website: organisation.website ?? '',
      offerTermsText: organisation.offerTermsText ?? '',
      iban: organisation.iban ?? '',
      bankName: organisation.bankName ?? '',
      bic: organisation.bic ?? ''
    };
    form.setValues(values);
    form.resetDirty(values);
  }, [organisation]);

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)} noValidate style={{ width: '100%' }}>
      <CardSection title={t('orgForm.formCardTitle')}>
        <Stack gap={24} p={{ base: 16, sm: 24 }}>
          <Stack gap={20} style={{ width: '100%' }}>
            <div style={fieldRowStyle}>
              <TextField label={t('orgForm.fieldNameLabel')} {...form.getInputProps('name')} />
              <TextField
                label={t('orgForm.fieldTaxIdLabel')}
                value={organisation.taxId ?? '—'}
                disabled
                helperText={t('orgForm.fieldTaxIdHelper')}
              />
            </div>

            <div style={fieldRowStyle}>
              <TextField label={t('orgForm.fieldPhoneLabel')} {...form.getInputProps('phone')} />
              <TextField label={t('orgForm.fieldEmailLabel')} {...form.getInputProps('email')} />
            </div>

            <div style={fieldRowStyle}>
              <TextField
                label={t('orgForm.fieldContactPersonLabel')}
                {...form.getInputProps('contactPerson')}
              />
              <TextField
                label={t('orgForm.fieldWebsiteLabel')}
                {...form.getInputProps('website')}
              />
            </div>

            <div style={fieldRowStyle}>
              <TextField label={t('orgForm.fieldIbanLabel')} {...form.getInputProps('iban')} />
              <TextField
                label={t('orgForm.fieldBankNameLabel')}
                {...form.getInputProps('bankName')}
              />
              <TextField label={t('orgForm.fieldBicLabel')} {...form.getInputProps('bic')} />
            </div>

            <div style={fieldRowStyle}>
              <TextField
                label={t('orgForm.fieldAddressLabel')}
                {...form.getInputProps('address')}
              />
              <TextField label={t('orgForm.fieldCityLabel')} {...form.getInputProps('city')} />
            </div>

            <div style={fieldRowStyle}>
              <TextField
                label={t('orgForm.fieldPostalCodeLabel')}
                {...form.getInputProps('postalCode')}
              />
              <SelectField
                label={t('orgForm.fieldCountryLabel')}
                allowDeselect={false}
                disabled
                helperText={t('orgForm.fieldCountryHelper')}
                data={countryOptions}
                value={organisation.country ?? null}
              />
            </div>

            <div style={fieldRowStyle}>
              <SelectField
                label={t('orgForm.fieldCurrencyLabel')}
                allowDeselect={false}
                searchable
                data={currencyOptions}
                {...form.getInputProps('currency')}
              />
              <Stack gap={6}>
                <Text {...fieldLabelStyle}>{t('orgForm.statusLabel')}</Text>
                <StatusPill
                  label={t(
                    organisation.isActive ? 'orgForm.statusActive' : 'orgForm.statusInactive'
                  )}
                  bg={
                    organisation.isActive
                      ? 'var(--color-status-success-soft)'
                      : 'var(--color-status-danger-soft)'
                  }
                  fg={
                    organisation.isActive
                      ? 'var(--color-status-success)'
                      : 'var(--color-status-danger)'
                  }
                />
              </Stack>
            </div>

            <TextareaField
              label={t('orgForm.fieldOfferTermsTextLabel')}
              minRows={3}
              helperText={t('orgForm.fieldOfferTermsTextHelper')}
              {...form.getInputProps('offerTermsText')}
            />

            <LogoPickerField
              initials={initials(form.values.name || organisation.name)}
              src={logoSrc}
              label={t('orgForm.changeLogo')}
              labelMobile={t('orgForm.changeLogoMobile')}
              hint={t('orgForm.logoHint')}
              hintMobile={t('orgForm.logoHintMobile')}
              onFileSelected={onLogoSelected}
              disabled={logoUploading}
            />
          </Stack>

          <Divider />

          <FormActions>
            {showCancel && (
              <Button variant="outline" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
            )}
            <MutationButton type="submit" isPending={loading} isPaused={isPaused}>
              {t('orgForm.saveButton')}
            </MutationButton>
          </FormActions>
        </Stack>
      </CardSection>
    </form>
  );
};
